import { TooltipProps } from '@mui/material';
import { tz } from 'dayjs';
import { ReactNode, useMemo } from 'react';
import { PLATFORM_PARTNER_ID } from 'src/constants/platform';
import { usePartners } from 'src/graphql';
import { useDisplayTime } from 'src/utils/hooks';
import TimeZoneTooltip, { TimeZoneTooltipProps } from './timezone-tooltip';

interface PartnerTimeZoneTooltipProps {
  date: string;
  partnerId?: number | null;
  children?: ReactNode;
  tooltipProps?: Partial<TooltipProps>;
}

type PartnerTimeZoneMap = Record<string, { timeZone?: string | null }>;

export const PartnerTimeZoneTooltip = ({
  date,
  partnerId,
  children,
  tooltipProps,
}: PartnerTimeZoneTooltipProps) => {
  const userTimezone = tz.guess();

  const { timeFormat } = useDisplayTime();

  const { partners: partnerMap } = usePartners();
  const partners = partnerMap as unknown as PartnerTimeZoneMap | undefined;

  const tzList = useMemo(() => {
    const list: TimeZoneTooltipProps['tzList'] = [];

    if (userTimezone) {
      list.push({ tz: userTimezone, label: 'Your computer', timeFormat });
    }

    const currentPartner = partners?.[partnerId!] ?? null;
    const systemPartner = partners?.[PLATFORM_PARTNER_ID];

    if (systemPartner?.timeZone) {
      list.push({
        tz: systemPartner.timeZone,
        label: 'Platform',
        timeFormat,
      });
    }

    if (currentPartner?.timeZone) {
      list.push({
        tz: currentPartner.timeZone,
        label: 'Partner',
        timeFormat,
      });
    }

    return list;
  }, [userTimezone, partners, partnerId, timeFormat]);

  return (
    <TimeZoneTooltip tzList={tzList} date={date} tooltipProps={tooltipProps}>
      {children}
    </TimeZoneTooltip>
  );
};

export default PartnerTimeZoneTooltip;
